/**
 * Modal de envío de recetas
 *
 * Ventana modal accesible que aloja el formulario de envío de recetas de
 * usuarios (RecipeSubmissionForm). Se monta mediante un portal en
 * `document.body` para quedar por encima de cualquier contenido de la
 * página y evitar problemas de apilamiento (z-index) con secciones que
 * usan `overflow: hidden` o `transform` (habitual en las páginas
 * inmersivas con GSAP).
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import RecipeSubmissionForm from './RecipeSubmissionForm';

/**
 * Modal de envío de recetas.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.abierto - Si el modal debe mostrarse.
 * @param {Function} props.onCerrar - Manejador que cierra el modal (gestiona
 *   también la URL a través de `useRecipeSubmissionModal`).
 * @returns {JSX.Element|null} El modal, o `null` cuando está cerrado.
 */
const RecipeSubmissionModal = ({ abierto, onCerrar }) => {
  const panelRef = useRef(null);
  const elementoConFocoPrevio = useRef(null);
  const overflowPrevio = useRef('');

  // El formulario avisa cuando el envío ha ido bien para que el modal esconda
  // su cabecera: dejar "Envíanos tu receta" encima de "¡Gracias! Hemos
  // recibido tu receta" es redundante y descentra el mensaje de confirmación.
  const { t } = useTranslation(['forms', 'common']);
  const [enviado, setEnviado] = useState(false);

  // Al cerrar se reinicia, de modo que la próxima apertura muestre de nuevo la
  // cabecera y un formulario limpio.
  useEffect(() => {
    if (!abierto) setEnviado(false);
  }, [abierto]);

  // Bloqueo de scroll de fondo: la web usa Lenis (scroll suave) expuesto
  // globalmente como `window.__lenis` (ver ScrollToTop.jsx). Con Lenis
  // activo, `overflow: hidden` en el body NO es suficiente para impedir el
  // scroll de fondo mientras el modal está abierto, así que también hay
  // que detener la instancia de Lenis explícitamente.
  //
  // En una entrada en frío a /envio-receta el modal nace abierto en el
  // primer render, pero `window.__lenis` lo crea `SmoothScrollProvider`
  // (dentro de ImmersiveLayout), que es un ANTECESOR de este modal en el
  // árbol. React ejecuta los efectos de los hijos antes que los del padre,
  // así que en ese primer render `window.__lenis` todavía no existe y un
  // simple `window.__lenis?.stop()` sería un no-op. Por eso, si Lenis aún
  // no existe, reintentamos brevemente hasta que `SmoothScrollProvider`
  // lo haya creado.
  useEffect(() => {
    if (!abierto) return undefined;

    overflowPrevio.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let intervaloEspera = null;
    if (window.__lenis) {
      window.__lenis.stop();
    } else {
      intervaloEspera = setInterval(() => {
        if (window.__lenis) {
          window.__lenis.stop();
          clearInterval(intervaloEspera);
          intervaloEspera = null;
        }
      }, 30);
    }

    return () => {
      if (intervaloEspera) clearInterval(intervaloEspera);
      window.__lenis?.start();
      document.body.style.overflow = overflowPrevio.current;
    };
  }, [abierto]);

  // Cursor nativo dentro del modal.
  //
  // `CustomCursor` (components/common/CustomCursor.jsx) inyecta en el <head>
  // una hoja de estilo con id `hide-native-cursor` cuya regla es
  // `*, *::before, *::after { cursor: none !important; }`. Ese `!important`
  // sobre el selector universal deja SIN cursor de texto a los campos del
  // formulario, y sin cursor de mano a los botones: en una página de
  // scrollytelling el cursor decorativo aporta, pero rellenando un formulario
  // el usuario necesita ver dónde va a escribir.
  //
  // Mientras el modal está abierto desactivamos esa hoja (`disabled = true`,
  // reversible y sin tocar el DOM ajeno) y marcamos el body para que el CSS
  // esconda el punto y el círculo decorativos. Al cerrar se restaura todo.
  useEffect(() => {
    if (!abierto) return undefined;

    const estiloCursor = document.getElementById('hide-native-cursor');
    const estabaActivo = estiloCursor ? !estiloCursor.disabled : false;
    if (estiloCursor) estiloCursor.disabled = true;
    document.body.classList.add('modal-formulario-abierto');

    return () => {
      if (estiloCursor && estabaActivo) estiloCursor.disabled = false;
      document.body.classList.remove('modal-formulario-abierto');
    };
  }, [abierto]);

  // Gestión de foco accesible: al abrir, guarda el elemento que tenía el
  // foco para poder devolvérselo al cerrar, y mueve el foco al panel del
  // modal. Al cerrar (o desmontar), restaura el foco original.
  useEffect(() => {
    if (!abierto) return undefined;

    elementoConFocoPrevio.current = document.activeElement;
    panelRef.current?.focus();

    return () => {
      elementoConFocoPrevio.current?.focus?.();
    };
  }, [abierto]);

  // Cierre con la tecla Escape.
  useEffect(() => {
    if (!abierto) return undefined;

    const manejarTeclado = (evento) => {
      if (evento.key === 'Escape') {
        onCerrar();
      }
    };

    document.addEventListener('keydown', manejarTeclado);
    return () => document.removeEventListener('keydown', manejarTeclado);
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  /**
   * Cierra el modal solo si el clic se produjo sobre el fondo (backdrop),
   * nunca cuando el clic ocurre dentro del panel del formulario.
   */
  const manejarClicFondo = (evento) => {
    if (evento.target === evento.currentTarget) {
      onCerrar();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-intercasa-dark/80 backdrop-blur-sm z-[90] flex items-center justify-center p-0 sm:p-4"
      onClick={manejarClicFondo}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-modal-envio-receta"
        tabIndex={-1}
        /*
         * `data-lenis-prevent` es IMPRESCINDIBLE para que la rueda del ratón
         * funcione dentro del modal. Lenis engancha un listener de `wheel` en
         * el documento y llama a `preventDefault()` en él, incluso estando
         * parado con `stop()`: parar a Lenis evita que se mueva el fondo, pero
         * el evento igualmente se consume y nunca llega al `overflow-y-auto`
         * de este panel, así que el formulario parecía no tener scroll.
         * Este atributo le dice a Lenis que ignore por completo los eventos
         * que nazcan dentro de este elemento y deje actuar al scroll nativo.
         */
        data-lenis-prevent
        className="bg-white rounded-none sm:rounded-2xl w-full h-full sm:h-auto max-w-2xl sm:max-h-[90vh] overflow-y-auto overscroll-contain p-6 md:p-10 relative focus:outline-none"
      >
        {/* Botón de cierre */}
        <button
          type="button"
          onClick={onCerrar}
          aria-label={t('common:actions.close')}
          className="absolute top-4 right-4 md:top-6 md:right-6 text-intercasa-dark/60 hover:text-intercasa-red transition-colors duration-300"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Cabecera: se oculta en la pantalla de confirmación, pero el <h2>
            debe seguir existiendo porque `aria-labelledby` del diálogo apunta
            a él. Por eso se mantiene con `sr-only` en lugar de desmontarlo. */}
        <div className={enviado ? 'sr-only' : 'mb-8 pr-10'}>
          <h2
            id="titulo-modal-envio-receta"
            className={
              enviado
                ? ''
                : 'font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-2'
            }
          >
            {t('forms:recipe.modal.title')}
          </h2>
          {!enviado && (
            <p className="font-lato text-intercasa-dark/70">
              {t('forms:recipe.modal.intro')}
            </p>
          )}
        </div>

        <RecipeSubmissionForm onEnviado={onCerrar} onEstadoEnvio={setEnviado} />
      </div>
    </div>,
    document.body
  );
};

RecipeSubmissionModal.propTypes = {
  abierto: PropTypes.bool.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

export default RecipeSubmissionModal;
